/**
 * Écran « Rapports » (`pages/rh/ReportsPage.tsx`), carte « Coût par complétion » du tableau CODIR
 * (`components/v2/rh/CODIRDashboard.tsx`) et encart « Validation » du tableau de bord manager
 * (`components/v2/manager/ManagerDashboard.tsx`) — 11 langues, fusionnées dans `lib/i18n.ts`.
 */
import type { UiLanguageCode } from '@/lib/ui-languages';

export const rhReportsLocales: Record<UiLanguageCode, Record<string, string>> = {
  fr: {
    'codir.card.costPerCompletion': 'Coût par complétion (année)',
    'codir.card.completions': '{{n}} achèvements',
    'manager.dashboard.pendingApprovalsHint':
      'Demandes dont vous êtes l’approbateur (ou le délégataire), en attente de votre décision.',
    'rhReports.type.codir': 'Synthèse CODIR',
    'rhReports.type.department': 'Budget par direction',
    'rhReports.type.custom': 'Personnalisé',
    'rhReports.section.summary': 'Synthèse (indicateurs clés)',
    'rhReports.section.completionTrend': 'Achèvements par mois',
    'rhReports.section.budgetByDepartment': 'Budget par direction',
    'rhReports.section.costPerCompletion': 'Coût par complétion (3 ans)',
    'rhReports.section.programs': 'Complétion par formation',
    'rhReports.section.competencyTrend': 'Compétences validées par mois',
    'rhReports.frequency.daily': 'Chaque jour (6 h)',
    'rhReports.frequency.weekly': 'Chaque lundi (6 h)',
    'rhReports.frequency.monthly': 'Le 1er du mois (6 h)',
    'rhReports.frequency.cron': 'Expression cron',
    'rhReports.frequency.cronValue': 'Cron : {{expr}}',
    'rhReports.loadError': 'Impossible de charger les rapports.',
    'rhReports.generateStarted': 'Génération lancée : le fichier sera disponible dans la liste.',
    'rhReports.generateError': 'La génération n’a pas pu être lancée.',
    'rhReports.downloadError': 'Téléchargement impossible.',
    'rhReports.deleteConfirm': 'Supprimer ce rapport et son fichier ?',
    'rhReports.deleteError': 'Suppression impossible.',
    'rhReports.scheduleSaved': 'Planification enregistrée.',
    'rhReports.scheduleError': 'Planification impossible.',
    'rhReports.runStarted': 'Exécution lancée.',
    'rhReports.scheduleDeleteConfirm': 'Supprimer cette planification ?',
    'rhReports.status.generating': 'En cours',
    'rhReports.status.failed': 'Échec',
    'rhReports.status.completed': 'Terminé',
    'rhReports.sections': 'Contenu du rapport',
    'rhReports.forbidden': 'Les rapports sont réservés à l’équipe RH et aux administrateurs de l’établissement.',
    'rhReports.generateTitle': 'Générer un rapport',
    'rhReports.fieldTitle': 'Titre',
    'rhReports.fieldType': 'Type',
    'rhReports.fieldFormat': 'Format',
    'rhReports.fieldFormats': 'Formats',
    'rhReports.fieldYear': 'Année (budget, coûts)',
    'rhReports.fieldFrequency': 'Fréquence',
    'rhReports.fieldCron': 'Expression cron (minute heure jour mois jour-semaine)',
    'rhReports.fieldRecipients': 'Destinataires e-mail (facultatif)',
    'rhReports.recipientsHint': 'Séparés par des virgules. Ils reçoivent un lien vers cet écran.',
    'rhReports.generate': 'Générer',
    'rhReports.listTitle': 'Rapports générés',
    'rhReports.empty': 'Aucun rapport pour le moment.',
    'rhReports.colTitle': 'Rapport',
    'rhReports.colStatus': 'Statut',
    'rhReports.colDate': 'Date',
    'rhReports.colSize': 'Taille',
    'rhReports.colActions': 'Actions',
    'rhReports.scheduled': 'planifié',
    'rhReports.download': 'Télécharger',
    'rhReports.delete': 'Supprimer',
    'rhReports.schedulesTitle': 'Rapports planifiés',
    'rhReports.recipientsCount': '{{n}} destinataire(s)',
    'rhReports.lastRun': 'Dernière exécution : {{d}}',
    'rhReports.neverRun': 'Jamais exécuté',
    'rhReports.active': 'Active',
    'rhReports.runNow': 'Exécuter',
    'rhReports.addSchedule': 'Planifier',
  },
  en: {
    'codir.card.costPerCompletion': 'Cost per completion (year)',
    'codir.card.completions': '{{n}} completions',
    'manager.dashboard.pendingApprovalsHint': 'Requests you approve (or approve as a delegate), awaiting your decision.',
    'rhReports.type.codir': 'Executive summary',
    'rhReports.type.department': 'Budget by department',
    'rhReports.type.custom': 'Custom',
    'rhReports.section.summary': 'Summary (key indicators)',
    'rhReports.section.completionTrend': 'Completions per month',
    'rhReports.section.budgetByDepartment': 'Budget by department',
    'rhReports.section.costPerCompletion': 'Cost per completion (3 years)',
    'rhReports.section.programs': 'Completion by training',
    'rhReports.section.competencyTrend': 'Validated skills per month',
    'rhReports.frequency.daily': 'Every day (6 am)',
    'rhReports.frequency.weekly': 'Every Monday (6 am)',
    'rhReports.frequency.monthly': '1st of the month (6 am)',
    'rhReports.frequency.cron': 'Cron expression',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'Unable to load reports.',
    'rhReports.generateStarted': 'Generation started: the file will appear in the list.',
    'rhReports.generateError': 'Generation could not be started.',
    'rhReports.downloadError': 'Download failed.',
    'rhReports.deleteConfirm': 'Delete this report and its file?',
    'rhReports.deleteError': 'Unable to delete.',
    'rhReports.scheduleSaved': 'Schedule saved.',
    'rhReports.scheduleError': 'Scheduling failed.',
    'rhReports.runStarted': 'Run started.',
    'rhReports.scheduleDeleteConfirm': 'Delete this schedule?',
    'rhReports.status.generating': 'In progress',
    'rhReports.status.failed': 'Failed',
    'rhReports.status.completed': 'Completed',
    'rhReports.sections': 'Report content',
    'rhReports.forbidden': 'Reports are available to the HR team and institution administrators only.',
    'rhReports.generateTitle': 'Generate a report',
    'rhReports.fieldTitle': 'Title',
    'rhReports.fieldType': 'Type',
    'rhReports.fieldFormat': 'Format',
    'rhReports.fieldFormats': 'Formats',
    'rhReports.fieldYear': 'Year (budget, costs)',
    'rhReports.fieldFrequency': 'Frequency',
    'rhReports.fieldCron': 'Cron expression (minute hour day month weekday)',
    'rhReports.fieldRecipients': 'Email recipients (optional)',
    'rhReports.recipientsHint': 'Comma-separated. They receive a link to this screen.',
    'rhReports.generate': 'Generate',
    'rhReports.listTitle': 'Generated reports',
    'rhReports.empty': 'No reports yet.',
    'rhReports.colTitle': 'Report',
    'rhReports.colStatus': 'Status',
    'rhReports.colDate': 'Date',
    'rhReports.colSize': 'Size',
    'rhReports.colActions': 'Actions',
    'rhReports.scheduled': 'scheduled',
    'rhReports.download': 'Download',
    'rhReports.delete': 'Delete',
    'rhReports.schedulesTitle': 'Scheduled reports',
    'rhReports.recipientsCount': '{{n}} recipient(s)',
    'rhReports.lastRun': 'Last run: {{d}}',
    'rhReports.neverRun': 'Never run',
    'rhReports.active': 'Active',
    'rhReports.runNow': 'Run',
    'rhReports.addSchedule': 'Schedule',
  },
  de: {
    'codir.card.costPerCompletion': 'Kosten pro Abschluss (Jahr)',
    'codir.card.completions': '{{n}} Abschlüsse',
    'manager.dashboard.pendingApprovalsHint':
      'Anfragen, die Sie (oder als Vertretung) genehmigen und die auf Ihre Entscheidung warten.',
    'rhReports.type.codir': 'Management-Übersicht',
    'rhReports.type.department': 'Budget nach Bereich',
    'rhReports.type.custom': 'Benutzerdefiniert',
    'rhReports.section.summary': 'Übersicht (Kennzahlen)',
    'rhReports.section.completionTrend': 'Abschlüsse pro Monat',
    'rhReports.section.budgetByDepartment': 'Budget nach Bereich',
    'rhReports.section.costPerCompletion': 'Kosten pro Abschluss (3 Jahre)',
    'rhReports.section.programs': 'Abschlussquote pro Schulung',
    'rhReports.section.competencyTrend': 'Bestätigte Kompetenzen pro Monat',
    'rhReports.frequency.daily': 'Täglich (6 Uhr)',
    'rhReports.frequency.weekly': 'Jeden Montag (6 Uhr)',
    'rhReports.frequency.monthly': 'Am 1. des Monats (6 Uhr)',
    'rhReports.frequency.cron': 'Cron-Ausdruck',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'Berichte konnten nicht geladen werden.',
    'rhReports.generateStarted': 'Erstellung gestartet: Die Datei erscheint in der Liste.',
    'rhReports.generateError': 'Die Erstellung konnte nicht gestartet werden.',
    'rhReports.downloadError': 'Download fehlgeschlagen.',
    'rhReports.deleteConfirm': 'Diesen Bericht und seine Datei löschen?',
    'rhReports.deleteError': 'Löschen nicht möglich.',
    'rhReports.scheduleSaved': 'Planung gespeichert.',
    'rhReports.scheduleError': 'Planung nicht möglich.',
    'rhReports.runStarted': 'Ausführung gestartet.',
    'rhReports.scheduleDeleteConfirm': 'Diese Planung löschen?',
    'rhReports.status.generating': 'Läuft',
    'rhReports.status.failed': 'Fehlgeschlagen',
    'rhReports.status.completed': 'Fertig',
    'rhReports.sections': 'Inhalt des Berichts',
    'rhReports.forbidden': 'Berichte sind dem HR-Team und den Administratoren der Einrichtung vorbehalten.',
    'rhReports.generateTitle': 'Bericht erstellen',
    'rhReports.fieldTitle': 'Titel',
    'rhReports.fieldType': 'Typ',
    'rhReports.fieldFormat': 'Format',
    'rhReports.fieldFormats': 'Formate',
    'rhReports.fieldYear': 'Jahr (Budget, Kosten)',
    'rhReports.fieldFrequency': 'Häufigkeit',
    'rhReports.fieldCron': 'Cron-Ausdruck (Minute Stunde Tag Monat Wochentag)',
    'rhReports.fieldRecipients': 'E-Mail-Empfänger (optional)',
    'rhReports.recipientsHint': 'Durch Kommas getrennt. Sie erhalten einen Link zu dieser Seite.',
    'rhReports.generate': 'Erstellen',
    'rhReports.listTitle': 'Erstellte Berichte',
    'rhReports.empty': 'Noch keine Berichte.',
    'rhReports.colTitle': 'Bericht',
    'rhReports.colStatus': 'Status',
    'rhReports.colDate': 'Datum',
    'rhReports.colSize': 'Größe',
    'rhReports.colActions': 'Aktionen',
    'rhReports.scheduled': 'geplant',
    'rhReports.download': 'Herunterladen',
    'rhReports.delete': 'Löschen',
    'rhReports.schedulesTitle': 'Geplante Berichte',
    'rhReports.recipientsCount': '{{n}} Empfänger',
    'rhReports.lastRun': 'Letzte Ausführung: {{d}}',
    'rhReports.neverRun': 'Noch nie ausgeführt',
    'rhReports.active': 'Aktiv',
    'rhReports.runNow': 'Ausführen',
    'rhReports.addSchedule': 'Planen',
  },
  es: {
    'codir.card.costPerCompletion': 'Coste por finalización (año)',
    'codir.card.completions': '{{n}} finalizaciones',
    'manager.dashboard.pendingApprovalsHint':
      'Solicitudes que usted aprueba (o aprueba por delegación) y que esperan su decisión.',
    'rhReports.type.codir': 'Síntesis de dirección',
    'rhReports.type.department': 'Presupuesto por departamento',
    'rhReports.type.custom': 'Personalizado',
    'rhReports.section.summary': 'Síntesis (indicadores clave)',
    'rhReports.section.completionTrend': 'Finalizaciones por mes',
    'rhReports.section.budgetByDepartment': 'Presupuesto por departamento',
    'rhReports.section.costPerCompletion': 'Coste por finalización (3 años)',
    'rhReports.section.programs': 'Finalización por formación',
    'rhReports.section.competencyTrend': 'Competencias validadas por mes',
    'rhReports.frequency.daily': 'Cada día (6 h)',
    'rhReports.frequency.weekly': 'Cada lunes (6 h)',
    'rhReports.frequency.monthly': 'El día 1 de cada mes (6 h)',
    'rhReports.frequency.cron': 'Expresión cron',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'No se pudieron cargar los informes.',
    'rhReports.generateStarted': 'Generación iniciada: el archivo aparecerá en la lista.',
    'rhReports.generateError': 'No se pudo iniciar la generación.',
    'rhReports.downloadError': 'No se pudo descargar.',
    'rhReports.deleteConfirm': '¿Eliminar este informe y su archivo?',
    'rhReports.deleteError': 'No se pudo eliminar.',
    'rhReports.scheduleSaved': 'Programación guardada.',
    'rhReports.scheduleError': 'No se pudo programar.',
    'rhReports.runStarted': 'Ejecución iniciada.',
    'rhReports.scheduleDeleteConfirm': '¿Eliminar esta programación?',
    'rhReports.status.generating': 'En curso',
    'rhReports.status.failed': 'Error',
    'rhReports.status.completed': 'Terminado',
    'rhReports.sections': 'Contenido del informe',
    'rhReports.forbidden': 'Los informes están reservados al equipo de RR. HH. y a los administradores del centro.',
    'rhReports.generateTitle': 'Generar un informe',
    'rhReports.fieldTitle': 'Título',
    'rhReports.fieldType': 'Tipo',
    'rhReports.fieldFormat': 'Formato',
    'rhReports.fieldFormats': 'Formatos',
    'rhReports.fieldYear': 'Año (presupuesto, costes)',
    'rhReports.fieldFrequency': 'Frecuencia',
    'rhReports.fieldCron': 'Expresión cron (minuto hora día mes día-semana)',
    'rhReports.fieldRecipients': 'Destinatarios de correo (opcional)',
    'rhReports.recipientsHint': 'Separados por comas. Reciben un enlace a esta pantalla.',
    'rhReports.generate': 'Generar',
    'rhReports.listTitle': 'Informes generados',
    'rhReports.empty': 'Todavía no hay informes.',
    'rhReports.colTitle': 'Informe',
    'rhReports.colStatus': 'Estado',
    'rhReports.colDate': 'Fecha',
    'rhReports.colSize': 'Tamaño',
    'rhReports.colActions': 'Acciones',
    'rhReports.scheduled': 'programado',
    'rhReports.download': 'Descargar',
    'rhReports.delete': 'Eliminar',
    'rhReports.schedulesTitle': 'Informes programados',
    'rhReports.recipientsCount': '{{n}} destinatario(s)',
    'rhReports.lastRun': 'Última ejecución: {{d}}',
    'rhReports.neverRun': 'Nunca ejecutado',
    'rhReports.active': 'Activa',
    'rhReports.runNow': 'Ejecutar',
    'rhReports.addSchedule': 'Programar',
  },
  it: {
    'codir.card.costPerCompletion': 'Costo per completamento (anno)',
    'codir.card.completions': '{{n}} completamenti',
    'manager.dashboard.pendingApprovalsHint':
      'Richieste di cui lei è l’approvatore (o il delegato), in attesa della sua decisione.',
    'rhReports.type.codir': 'Sintesi per la direzione',
    'rhReports.type.department': 'Budget per direzione',
    'rhReports.type.custom': 'Personalizzato',
    'rhReports.section.summary': 'Sintesi (indicatori chiave)',
    'rhReports.section.completionTrend': 'Completamenti per mese',
    'rhReports.section.budgetByDepartment': 'Budget per direzione',
    'rhReports.section.costPerCompletion': 'Costo per completamento (3 anni)',
    'rhReports.section.programs': 'Completamento per corso',
    'rhReports.section.competencyTrend': 'Competenze convalidate per mese',
    'rhReports.frequency.daily': 'Ogni giorno (ore 6)',
    'rhReports.frequency.weekly': 'Ogni lunedì (ore 6)',
    'rhReports.frequency.monthly': 'Il 1° del mese (ore 6)',
    'rhReports.frequency.cron': 'Espressione cron',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'Impossibile caricare i report.',
    'rhReports.generateStarted': 'Generazione avviata: il file comparirà nell’elenco.',
    'rhReports.generateError': 'Impossibile avviare la generazione.',
    'rhReports.downloadError': 'Download non riuscito.',
    'rhReports.deleteConfirm': 'Eliminare questo report e il relativo file?',
    'rhReports.deleteError': 'Eliminazione non riuscita.',
    'rhReports.scheduleSaved': 'Pianificazione salvata.',
    'rhReports.scheduleError': 'Pianificazione non riuscita.',
    'rhReports.runStarted': 'Esecuzione avviata.',
    'rhReports.scheduleDeleteConfirm': 'Eliminare questa pianificazione?',
    'rhReports.status.generating': 'In corso',
    'rhReports.status.failed': 'Non riuscito',
    'rhReports.status.completed': 'Completato',
    'rhReports.sections': 'Contenuto del report',
    'rhReports.forbidden': 'I report sono riservati al team HR e agli amministratori dell’ente.',
    'rhReports.generateTitle': 'Genera un report',
    'rhReports.fieldTitle': 'Titolo',
    'rhReports.fieldType': 'Tipo',
    'rhReports.fieldFormat': 'Formato',
    'rhReports.fieldFormats': 'Formati',
    'rhReports.fieldYear': 'Anno (budget, costi)',
    'rhReports.fieldFrequency': 'Frequenza',
    'rhReports.fieldCron': 'Espressione cron (minuto ora giorno mese giorno-settimana)',
    'rhReports.fieldRecipients': 'Destinatari e-mail (facoltativo)',
    'rhReports.recipientsHint': 'Separati da virgole. Ricevono un link a questa schermata.',
    'rhReports.generate': 'Genera',
    'rhReports.listTitle': 'Report generati',
    'rhReports.empty': 'Nessun report per ora.',
    'rhReports.colTitle': 'Report',
    'rhReports.colStatus': 'Stato',
    'rhReports.colDate': 'Data',
    'rhReports.colSize': 'Dimensione',
    'rhReports.colActions': 'Azioni',
    'rhReports.scheduled': 'pianificato',
    'rhReports.download': 'Scarica',
    'rhReports.delete': 'Elimina',
    'rhReports.schedulesTitle': 'Report pianificati',
    'rhReports.recipientsCount': '{{n}} destinatario/i',
    'rhReports.lastRun': 'Ultima esecuzione: {{d}}',
    'rhReports.neverRun': 'Mai eseguito',
    'rhReports.active': 'Attiva',
    'rhReports.runNow': 'Esegui',
    'rhReports.addSchedule': 'Pianifica',
  },
  pt: {
    'codir.card.costPerCompletion': 'Custo por conclusão (ano)',
    'codir.card.completions': '{{n}} conclusões',
    'manager.dashboard.pendingApprovalsHint':
      'Pedidos de que é o aprovador (ou o delegado), a aguardar a sua decisão.',
    'rhReports.type.codir': 'Síntese para a direção',
    'rhReports.type.department': 'Orçamento por direção',
    'rhReports.type.custom': 'Personalizado',
    'rhReports.section.summary': 'Síntese (indicadores-chave)',
    'rhReports.section.completionTrend': 'Conclusões por mês',
    'rhReports.section.budgetByDepartment': 'Orçamento por direção',
    'rhReports.section.costPerCompletion': 'Custo por conclusão (3 anos)',
    'rhReports.section.programs': 'Conclusão por formação',
    'rhReports.section.competencyTrend': 'Competências validadas por mês',
    'rhReports.frequency.daily': 'Todos os dias (6 h)',
    'rhReports.frequency.weekly': 'Todas as segundas-feiras (6 h)',
    'rhReports.frequency.monthly': 'No dia 1 de cada mês (6 h)',
    'rhReports.frequency.cron': 'Expressão cron',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'Não foi possível carregar os relatórios.',
    'rhReports.generateStarted': 'Geração iniciada: o ficheiro aparecerá na lista.',
    'rhReports.generateError': 'Não foi possível iniciar a geração.',
    'rhReports.downloadError': 'Não foi possível transferir.',
    'rhReports.deleteConfirm': 'Eliminar este relatório e o respetivo ficheiro?',
    'rhReports.deleteError': 'Não foi possível eliminar.',
    'rhReports.scheduleSaved': 'Agendamento guardado.',
    'rhReports.scheduleError': 'Não foi possível agendar.',
    'rhReports.runStarted': 'Execução iniciada.',
    'rhReports.scheduleDeleteConfirm': 'Eliminar este agendamento?',
    'rhReports.status.generating': 'Em curso',
    'rhReports.status.failed': 'Falhou',
    'rhReports.status.completed': 'Concluído',
    'rhReports.sections': 'Conteúdo do relatório',
    'rhReports.forbidden': 'Os relatórios estão reservados à equipa de RH e aos administradores da instituição.',
    'rhReports.generateTitle': 'Gerar um relatório',
    'rhReports.fieldTitle': 'Título',
    'rhReports.fieldType': 'Tipo',
    'rhReports.fieldFormat': 'Formato',
    'rhReports.fieldFormats': 'Formatos',
    'rhReports.fieldYear': 'Ano (orçamento, custos)',
    'rhReports.fieldFrequency': 'Frequência',
    'rhReports.fieldCron': 'Expressão cron (minuto hora dia mês dia-da-semana)',
    'rhReports.fieldRecipients': 'Destinatários de e-mail (opcional)',
    'rhReports.recipientsHint': 'Separados por vírgulas. Recebem uma ligação para este ecrã.',
    'rhReports.generate': 'Gerar',
    'rhReports.listTitle': 'Relatórios gerados',
    'rhReports.empty': 'Ainda não há relatórios.',
    'rhReports.colTitle': 'Relatório',
    'rhReports.colStatus': 'Estado',
    'rhReports.colDate': 'Data',
    'rhReports.colSize': 'Tamanho',
    'rhReports.colActions': 'Ações',
    'rhReports.scheduled': 'agendado',
    'rhReports.download': 'Transferir',
    'rhReports.delete': 'Eliminar',
    'rhReports.schedulesTitle': 'Relatórios agendados',
    'rhReports.recipientsCount': '{{n}} destinatário(s)',
    'rhReports.lastRun': 'Última execução: {{d}}',
    'rhReports.neverRun': 'Nunca executado',
    'rhReports.active': 'Ativo',
    'rhReports.runNow': 'Executar',
    'rhReports.addSchedule': 'Agendar',
  },
  lb: {
    'codir.card.costPerCompletion': 'Käschte pro Ofschloss (Joer)',
    'codir.card.completions': '{{n}} Ofschlëss',
    'manager.dashboard.pendingApprovalsHint':
      'Ufroen, déi Dir (oder als Vertriedung) guttheescht an déi op Är Decisioun waarden.',
    'rhReports.type.codir': 'Iwwersiicht fir d’Direktioun',
    'rhReports.type.department': 'Budget no Direktioun',
    'rhReports.type.custom': 'Personaliséiert',
    'rhReports.section.summary': 'Iwwersiicht (Schlësselindikatoren)',
    'rhReports.section.completionTrend': 'Ofschlëss pro Mount',
    'rhReports.section.budgetByDepartment': 'Budget no Direktioun',
    'rhReports.section.costPerCompletion': 'Käschte pro Ofschloss (3 Joer)',
    'rhReports.section.programs': 'Ofschloss pro Formatioun',
    'rhReports.section.competencyTrend': 'Validéiert Kompetenzen pro Mount',
    'rhReports.frequency.daily': 'All Dag (6 Auer)',
    'rhReports.frequency.weekly': 'All Méindeg (6 Auer)',
    'rhReports.frequency.monthly': 'Den 1. vum Mount (6 Auer)',
    'rhReports.frequency.cron': 'Cron-Ausdrock',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'D’Rapporte konnten net gelueden ginn.',
    'rhReports.generateStarted': 'Generatioun gestart: de Fichier erschéngt an der Lëscht.',
    'rhReports.generateError': 'D’Generatioun konnt net gestart ginn.',
    'rhReports.downloadError': 'Eroflueden net méiglech.',
    'rhReports.deleteConfirm': 'Dëse Rapport a säi Fichier läschen?',
    'rhReports.deleteError': 'Läschen net méiglech.',
    'rhReports.scheduleSaved': 'Planung gespäichert.',
    'rhReports.scheduleError': 'Planung net méiglech.',
    'rhReports.runStarted': 'Ausféierung gestart.',
    'rhReports.scheduleDeleteConfirm': 'Dës Planung läschen?',
    'rhReports.status.generating': 'Amgaang',
    'rhReports.status.failed': 'Feeler',
    'rhReports.status.completed': 'Fäerdeg',
    'rhReports.sections': 'Inhalt vum Rapport',
    'rhReports.forbidden': 'D’Rapporte sinn dem HR-Team an den Administrateure vun der Institutioun reservéiert.',
    'rhReports.generateTitle': 'E Rapport generéieren',
    'rhReports.fieldTitle': 'Titel',
    'rhReports.fieldType': 'Typ',
    'rhReports.fieldFormat': 'Format',
    'rhReports.fieldFormats': 'Formater',
    'rhReports.fieldYear': 'Joer (Budget, Käschten)',
    'rhReports.fieldFrequency': 'Frequenz',
    'rhReports.fieldCron': 'Cron-Ausdrock (Minutt Stonn Dag Mount Wochendag)',
    'rhReports.fieldRecipients': 'E-Mail-Empfänger (fakultativ)',
    'rhReports.recipientsHint': 'Mat Kommaen getrennt. Si kréien e Link op dësen Ecran.',
    'rhReports.generate': 'Generéieren',
    'rhReports.listTitle': 'Generéiert Rapporten',
    'rhReports.empty': 'Nach keng Rapporten.',
    'rhReports.colTitle': 'Rapport',
    'rhReports.colStatus': 'Status',
    'rhReports.colDate': 'Datum',
    'rhReports.colSize': 'Gréisst',
    'rhReports.colActions': 'Aktiounen',
    'rhReports.scheduled': 'geplangt',
    'rhReports.download': 'Eroflueden',
    'rhReports.delete': 'Läschen',
    'rhReports.schedulesTitle': 'Geplangte Rapporten',
    'rhReports.recipientsCount': '{{n}} Empfänger',
    'rhReports.lastRun': 'Lescht Ausféierung: {{d}}',
    'rhReports.neverRun': 'Nach ni ausgefouert',
    'rhReports.active': 'Aktiv',
    'rhReports.runNow': 'Ausféieren',
    'rhReports.addSchedule': 'Plangen',
  },
  uk: {
    'codir.card.costPerCompletion': 'Вартість одного завершення (рік)',
    'codir.card.completions': 'Завершень: {{n}}',
    'manager.dashboard.pendingApprovalsHint':
      'Запити, які затверджуєте ви (або як заступник) і які чекають на ваше рішення.',
    'rhReports.type.codir': 'Зведення для керівництва',
    'rhReports.type.department': 'Бюджет за підрозділами',
    'rhReports.type.custom': 'Власний',
    'rhReports.section.summary': 'Зведення (ключові показники)',
    'rhReports.section.completionTrend': 'Завершення за місяцями',
    'rhReports.section.budgetByDepartment': 'Бюджет за підрозділами',
    'rhReports.section.costPerCompletion': 'Вартість одного завершення (3 роки)',
    'rhReports.section.programs': 'Завершення за навчальними курсами',
    'rhReports.section.competencyTrend': 'Підтверджені компетенції за місяцями',
    'rhReports.frequency.daily': 'Щодня (6:00)',
    'rhReports.frequency.weekly': 'Щопонеділка (6:00)',
    'rhReports.frequency.monthly': '1-го числа місяця (6:00)',
    'rhReports.frequency.cron': 'Вираз cron',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'Не вдалося завантажити звіти.',
    'rhReports.generateStarted': 'Створення розпочато: файл з’явиться у списку.',
    'rhReports.generateError': 'Не вдалося розпочати створення.',
    'rhReports.downloadError': 'Не вдалося завантажити файл.',
    'rhReports.deleteConfirm': 'Видалити цей звіт і його файл?',
    'rhReports.deleteError': 'Не вдалося видалити.',
    'rhReports.scheduleSaved': 'Розклад збережено.',
    'rhReports.scheduleError': 'Не вдалося запланувати.',
    'rhReports.runStarted': 'Виконання розпочато.',
    'rhReports.scheduleDeleteConfirm': 'Видалити цей розклад?',
    'rhReports.status.generating': 'Триває',
    'rhReports.status.failed': 'Помилка',
    'rhReports.status.completed': 'Готово',
    'rhReports.sections': 'Зміст звіту',
    'rhReports.forbidden': 'Звіти доступні лише команді HR та адміністраторам закладу.',
    'rhReports.generateTitle': 'Створити звіт',
    'rhReports.fieldTitle': 'Назва',
    'rhReports.fieldType': 'Тип',
    'rhReports.fieldFormat': 'Формат',
    'rhReports.fieldFormats': 'Формати',
    'rhReports.fieldYear': 'Рік (бюджет, витрати)',
    'rhReports.fieldFrequency': 'Періодичність',
    'rhReports.fieldCron': 'Вираз cron (хвилина година день місяць день-тижня)',
    'rhReports.fieldRecipients': 'Одержувачі e-mail (необов’язково)',
    'rhReports.recipientsHint': 'Через кому. Вони отримають посилання на цей екран.',
    'rhReports.generate': 'Створити',
    'rhReports.listTitle': 'Створені звіти',
    'rhReports.empty': 'Звітів поки немає.',
    'rhReports.colTitle': 'Звіт',
    'rhReports.colStatus': 'Статус',
    'rhReports.colDate': 'Дата',
    'rhReports.colSize': 'Розмір',
    'rhReports.colActions': 'Дії',
    'rhReports.scheduled': 'за розкладом',
    'rhReports.download': 'Завантажити',
    'rhReports.delete': 'Видалити',
    'rhReports.schedulesTitle': 'Заплановані звіти',
    'rhReports.recipientsCount': 'Одержувачів: {{n}}',
    'rhReports.lastRun': 'Останнє виконання: {{d}}',
    'rhReports.neverRun': 'Ще не виконувався',
    'rhReports.active': 'Активний',
    'rhReports.runNow': 'Виконати',
    'rhReports.addSchedule': 'Запланувати',
  },
  ru: {
    'codir.card.costPerCompletion': 'Стоимость одного завершения (год)',
    'codir.card.completions': 'Завершений: {{n}}',
    'manager.dashboard.pendingApprovalsHint':
      'Заявки, которые утверждаете вы (или как заместитель) и которые ждут вашего решения.',
    'rhReports.type.codir': 'Сводка для руководства',
    'rhReports.type.department': 'Бюджет по подразделениям',
    'rhReports.type.custom': 'Настраиваемый',
    'rhReports.section.summary': 'Сводка (ключевые показатели)',
    'rhReports.section.completionTrend': 'Завершения по месяцам',
    'rhReports.section.budgetByDepartment': 'Бюджет по подразделениям',
    'rhReports.section.costPerCompletion': 'Стоимость одного завершения (3 года)',
    'rhReports.section.programs': 'Завершение по курсам',
    'rhReports.section.competencyTrend': 'Подтверждённые компетенции по месяцам',
    'rhReports.frequency.daily': 'Ежедневно (6:00)',
    'rhReports.frequency.weekly': 'Каждый понедельник (6:00)',
    'rhReports.frequency.monthly': '1-го числа месяца (6:00)',
    'rhReports.frequency.cron': 'Выражение cron',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'Не удалось загрузить отчёты.',
    'rhReports.generateStarted': 'Создание запущено: файл появится в списке.',
    'rhReports.generateError': 'Не удалось запустить создание.',
    'rhReports.downloadError': 'Не удалось скачать файл.',
    'rhReports.deleteConfirm': 'Удалить этот отчёт и его файл?',
    'rhReports.deleteError': 'Не удалось удалить.',
    'rhReports.scheduleSaved': 'Расписание сохранено.',
    'rhReports.scheduleError': 'Не удалось запланировать.',
    'rhReports.runStarted': 'Выполнение запущено.',
    'rhReports.scheduleDeleteConfirm': 'Удалить это расписание?',
    'rhReports.status.generating': 'Выполняется',
    'rhReports.status.failed': 'Ошибка',
    'rhReports.status.completed': 'Готово',
    'rhReports.sections': 'Содержание отчёта',
    'rhReports.forbidden': 'Отчёты доступны только команде HR и администраторам организации.',
    'rhReports.generateTitle': 'Создать отчёт',
    'rhReports.fieldTitle': 'Название',
    'rhReports.fieldType': 'Тип',
    'rhReports.fieldFormat': 'Формат',
    'rhReports.fieldFormats': 'Форматы',
    'rhReports.fieldYear': 'Год (бюджет, затраты)',
    'rhReports.fieldFrequency': 'Периодичность',
    'rhReports.fieldCron': 'Выражение cron (минута час день месяц день-недели)',
    'rhReports.fieldRecipients': 'Получатели e-mail (необязательно)',
    'rhReports.recipientsHint': 'Через запятую. Они получат ссылку на этот экран.',
    'rhReports.generate': 'Создать',
    'rhReports.listTitle': 'Созданные отчёты',
    'rhReports.empty': 'Отчётов пока нет.',
    'rhReports.colTitle': 'Отчёт',
    'rhReports.colStatus': 'Статус',
    'rhReports.colDate': 'Дата',
    'rhReports.colSize': 'Размер',
    'rhReports.colActions': 'Действия',
    'rhReports.scheduled': 'по расписанию',
    'rhReports.download': 'Скачать',
    'rhReports.delete': 'Удалить',
    'rhReports.schedulesTitle': 'Запланированные отчёты',
    'rhReports.recipientsCount': 'Получателей: {{n}}',
    'rhReports.lastRun': 'Последнее выполнение: {{d}}',
    'rhReports.neverRun': 'Ещё не выполнялся',
    'rhReports.active': 'Активно',
    'rhReports.runNow': 'Выполнить',
    'rhReports.addSchedule': 'Запланировать',
  },
  ar: {
    'codir.card.costPerCompletion': 'تكلفة كل إتمام (السنة)',
    'codir.card.completions': 'عدد الإتمامات: {{n}}',
    'manager.dashboard.pendingApprovalsHint': 'طلبات أنت المعتمِد لها (أو المفوَّض)، بانتظار قرارك.',
    'rhReports.type.codir': 'ملخص للإدارة',
    'rhReports.type.department': 'الميزانية حسب الإدارة',
    'rhReports.type.custom': 'مخصّص',
    'rhReports.section.summary': 'ملخص (المؤشرات الرئيسية)',
    'rhReports.section.completionTrend': 'الإتمامات شهريًا',
    'rhReports.section.budgetByDepartment': 'الميزانية حسب الإدارة',
    'rhReports.section.costPerCompletion': 'تكلفة كل إتمام (3 سنوات)',
    'rhReports.section.programs': 'الإتمام حسب التدريب',
    'rhReports.section.competencyTrend': 'الكفاءات المعتمدة شهريًا',
    'rhReports.frequency.daily': 'يوميًا (6 صباحًا)',
    'rhReports.frequency.weekly': 'كل يوم اثنين (6 صباحًا)',
    'rhReports.frequency.monthly': 'أول كل شهر (6 صباحًا)',
    'rhReports.frequency.cron': 'تعبير cron',
    'rhReports.frequency.cronValue': 'cron: {{expr}}',
    'rhReports.loadError': 'تعذّر تحميل التقارير.',
    'rhReports.generateStarted': 'بدأ الإنشاء: سيظهر الملف في القائمة.',
    'rhReports.generateError': 'تعذّر بدء الإنشاء.',
    'rhReports.downloadError': 'تعذّر التنزيل.',
    'rhReports.deleteConfirm': 'حذف هذا التقرير وملفه؟',
    'rhReports.deleteError': 'تعذّر الحذف.',
    'rhReports.scheduleSaved': 'تم حفظ الجدولة.',
    'rhReports.scheduleError': 'تعذّرت الجدولة.',
    'rhReports.runStarted': 'بدأ التنفيذ.',
    'rhReports.scheduleDeleteConfirm': 'حذف هذه الجدولة؟',
    'rhReports.status.generating': 'قيد الإنشاء',
    'rhReports.status.failed': 'فشل',
    'rhReports.status.completed': 'مكتمل',
    'rhReports.sections': 'محتوى التقرير',
    'rhReports.forbidden': 'التقارير مخصّصة لفريق الموارد البشرية ومسؤولي المؤسسة.',
    'rhReports.generateTitle': 'إنشاء تقرير',
    'rhReports.fieldTitle': 'العنوان',
    'rhReports.fieldType': 'النوع',
    'rhReports.fieldFormat': 'الصيغة',
    'rhReports.fieldFormats': 'الصيغ',
    'rhReports.fieldYear': 'السنة (الميزانية، التكاليف)',
    'rhReports.fieldFrequency': 'التكرار',
    'rhReports.fieldCron': 'تعبير cron (دقيقة ساعة يوم شهر يوم-الأسبوع)',
    'rhReports.fieldRecipients': 'المستلمون بالبريد الإلكتروني (اختياري)',
    'rhReports.recipientsHint': 'افصل بينهم بفواصل. يتلقّون رابطًا إلى هذه الشاشة.',
    'rhReports.generate': 'إنشاء',
    'rhReports.listTitle': 'التقارير المُنشأة',
    'rhReports.empty': 'لا توجد تقارير بعد.',
    'rhReports.colTitle': 'التقرير',
    'rhReports.colStatus': 'الحالة',
    'rhReports.colDate': 'التاريخ',
    'rhReports.colSize': 'الحجم',
    'rhReports.colActions': 'الإجراءات',
    'rhReports.scheduled': 'مجدول',
    'rhReports.download': 'تنزيل',
    'rhReports.delete': 'حذف',
    'rhReports.schedulesTitle': 'التقارير المجدولة',
    'rhReports.recipientsCount': 'عدد المستلمين: {{n}}',
    'rhReports.lastRun': 'آخر تنفيذ: {{d}}',
    'rhReports.neverRun': 'لم يُنفَّذ بعد',
    'rhReports.active': 'نشطة',
    'rhReports.runNow': 'تنفيذ',
    'rhReports.addSchedule': 'جدولة',
  },
  tr: {
    'codir.card.costPerCompletion': 'Tamamlama başına maliyet (yıl)',
    'codir.card.completions': '{{n}} tamamlama',
    'manager.dashboard.pendingApprovalsHint':
      'Onaylayıcısı (veya vekili) olduğunuz ve kararınızı bekleyen talepler.',
    'rhReports.type.codir': 'Yönetim özeti',
    'rhReports.type.department': 'Birime göre bütçe',
    'rhReports.type.custom': 'Özel',
    'rhReports.section.summary': 'Özet (temel göstergeler)',
    'rhReports.section.completionTrend': 'Aylık tamamlamalar',
    'rhReports.section.budgetByDepartment': 'Birime göre bütçe',
    'rhReports.section.costPerCompletion': 'Tamamlama başına maliyet (3 yıl)',
    'rhReports.section.programs': 'Eğitime göre tamamlama',
    'rhReports.section.competencyTrend': 'Aylık doğrulanan yetkinlikler',
    'rhReports.frequency.daily': 'Her gün (06.00)',
    'rhReports.frequency.weekly': 'Her pazartesi (06.00)',
    'rhReports.frequency.monthly': 'Her ayın 1’i (06.00)',
    'rhReports.frequency.cron': 'Cron ifadesi',
    'rhReports.frequency.cronValue': 'Cron: {{expr}}',
    'rhReports.loadError': 'Raporlar yüklenemedi.',
    'rhReports.generateStarted': 'Oluşturma başlatıldı: dosya listede görünecek.',
    'rhReports.generateError': 'Oluşturma başlatılamadı.',
    'rhReports.downloadError': 'İndirme başarısız.',
    'rhReports.deleteConfirm': 'Bu rapor ve dosyası silinsin mi?',
    'rhReports.deleteError': 'Silinemedi.',
    'rhReports.scheduleSaved': 'Zamanlama kaydedildi.',
    'rhReports.scheduleError': 'Zamanlama yapılamadı.',
    'rhReports.runStarted': 'Çalıştırma başlatıldı.',
    'rhReports.scheduleDeleteConfirm': 'Bu zamanlama silinsin mi?',
    'rhReports.status.generating': 'Sürüyor',
    'rhReports.status.failed': 'Başarısız',
    'rhReports.status.completed': 'Tamamlandı',
    'rhReports.sections': 'Rapor içeriği',
    'rhReports.forbidden': 'Raporlar yalnızca İK ekibine ve kurum yöneticilerine açıktır.',
    'rhReports.generateTitle': 'Rapor oluştur',
    'rhReports.fieldTitle': 'Başlık',
    'rhReports.fieldType': 'Tür',
    'rhReports.fieldFormat': 'Biçim',
    'rhReports.fieldFormats': 'Biçimler',
    'rhReports.fieldYear': 'Yıl (bütçe, maliyetler)',
    'rhReports.fieldFrequency': 'Sıklık',
    'rhReports.fieldCron': 'Cron ifadesi (dakika saat gün ay haftanın-günü)',
    'rhReports.fieldRecipients': 'E-posta alıcıları (isteğe bağlı)',
    'rhReports.recipientsHint': 'Virgülle ayırın. Bu ekrana bir bağlantı alırlar.',
    'rhReports.generate': 'Oluştur',
    'rhReports.listTitle': 'Oluşturulan raporlar',
    'rhReports.empty': 'Henüz rapor yok.',
    'rhReports.colTitle': 'Rapor',
    'rhReports.colStatus': 'Durum',
    'rhReports.colDate': 'Tarih',
    'rhReports.colSize': 'Boyut',
    'rhReports.colActions': 'İşlemler',
    'rhReports.scheduled': 'zamanlanmış',
    'rhReports.download': 'İndir',
    'rhReports.delete': 'Sil',
    'rhReports.schedulesTitle': 'Zamanlanmış raporlar',
    'rhReports.recipientsCount': '{{n}} alıcı',
    'rhReports.lastRun': 'Son çalıştırma: {{d}}',
    'rhReports.neverRun': 'Hiç çalıştırılmadı',
    'rhReports.active': 'Etkin',
    'rhReports.runNow': 'Çalıştır',
    'rhReports.addSchedule': 'Zamanla',
  },
};
